import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type IssueReviewStatus = 'pending' | 'resolved' | 'dismissed';

export type IssueReviewListItem = {
  id: string;
  organization: { id: string; name: string | null };
  issue: {
    id: string;
    title: string;
    severity: string;
    status: string;
    categoryKey: string;
  };
  reason: string;
  status: IssueReviewStatus;
  flaggedAt: string;
  flaggedBy: { displayName: string | null; primaryEmail: string | null } | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
};

type IssueReviewsResponse = {
  ok: boolean;
  data: IssueReviewListItem[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
};

export function useIssueReviewsQuery(params?: {
  status?: IssueReviewStatus;
  orgId?: string;
  page?: number;
  pageSize?: number;
}) {
  return useQuery({
    queryKey: ['superadmin-issue-reviews', params],
    queryFn: async (): Promise<IssueReviewsResponse> => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set('status', params.status);
      if (params?.orgId) searchParams.set('orgId', params.orgId);
      if (params?.page) searchParams.set('page', params.page.toString());
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());

      const res = await fetch(
        `${API_URL}/superadmin/issue-reviews?${searchParams.toString()}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw await toApiError(res, 'Failed to load issue reviews');
      }
      return res.json();
    },
  });
}

export function useFlagIssueMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { issueId: string; reason: string }) => {
      const res = await fetch(`${API_URL}/superadmin/issue-reviews`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to flag issue');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-issue-reviews'] }).catch(() => {});
    },
  });
}

export function useResolveReviewMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: 'resolved' | 'dismissed';
      resolutionNote?: string;
    }) => {
      const res = await fetch(`${API_URL}/superadmin/issue-reviews/${input.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          status: input.status,
          resolutionNote: input.resolutionNote,
        }),
      });
      if (!res.ok) {
        throw await toApiError(res, 'Failed to resolve review');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['superadmin-issue-reviews'] }).catch(() => {});
    },
  });
}
